import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useWeather } from "../weather/WeatherContext";
import { gustyWind } from "../weather/bostonWeather";

// Easter egg: skill icons can be grabbed, thrown, and bounced around the screen.
// Boston's wind blows them about, and when it snows they gather snow while still.

const PlaygroundContext = createContext(null);

const GRAVITY = 2200; // px/s^2
const BOUNCE = 0.7; // energy kept on impact
const MAX_SPEED = 4500; // px/s cap on throws
const SAMPLE_WINDOW = 90; // ms of pointer history used for throw velocity
const WIND_PUSH = 5.6; // px/s^2 of push per mph of wind
const WIND_TO_ROLL = 45; // px/s^2 the wind needs before it can roll an icon along the floor
const SNOW_SECONDS = 20; // time for a still icon to get a full cap of snow
const SHAKE_SPEED = 900; // px/s at which an icon sheds its snow

export function IconPlayground({ children }) {
  const [loose, setLoose] = useState([]); // icons currently out of their slots
  const bodies = useRef(new Map()); // id -> physics state
  const drag = useRef(null); // { id, offX, offY, samples }
  const frame = useRef(null);
  const lastTime = useRef(null);

  // Read by the animation loop, which is created once.
  const weather = useWeather();
  const scene = useRef(null);
  scene.current = weather?.animate ? weather.scene : null;

  const render = (b) => {
    if (b.el) {
      b.el.style.transform = `translate3d(${b.x}px, ${b.y}px, 0)`;
      b.img.style.transform = `rotate(${b.angle}deg)`;
      // The snow cap stays upright on top while the icon rolls underneath it.
      b.cap.style.height = `${b.snow * b.h * 0.3}px`;
      b.cap.style.opacity = b.snow > 0.03 ? 1 : 0;
    }
  };

  const tick = useCallback((now) => {
    const dt = Math.min((now - (lastTime.current ?? now)) / 1000, 1 / 30);
    lastTime.current = now;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const list = [...bodies.current.values()];
    const sky = scene.current;
    const windPush = sky ? gustyWind(sky, now / 1000) * WIND_PUSH : 0;
    const snowing = sky && (sky.kind === "snow" || sky.kind === "noreaster");

    for (const b of list) {
      const speed = Math.hypot(b.vx, b.vy);
      if (speed > SHAKE_SPEED) b.snow = Math.max(0, b.snow - dt * 3);
      else if (snowing && speed < 30) b.snow = Math.min(1, b.snow + (dt / SNOW_SECONDS) * (0.5 + sky.intensity / 2));

      if (b.dragging) continue;
      // Resting icons only roll once the wind is strong enough, so a breeze leaves them be.
      const onFloor = b.y + b.h >= H - 0.5 && b.vy === 0;
      if (!onFloor || Math.abs(windPush) > WIND_TO_ROLL) b.vx += windPush * dt;
      b.vy += GRAVITY * dt;
      b.vx *= Math.pow(0.7, dt); // light air drag
      b.x += b.vx * dt;
      b.y += b.vy * dt;

      if (b.x < 0) { b.x = 0; b.vx = -b.vx * BOUNCE; }
      if (b.x + b.w > W) { b.x = W - b.w; b.vx = -b.vx * BOUNCE; }
      if (b.y < 0) { b.y = 0; b.vy = -b.vy * BOUNCE; }
      if (b.y + b.h > H) {
        b.y = H - b.h;
        b.vy = Math.abs(b.vy) < 60 ? 0 : -b.vy * BOUNCE;
        b.vx *= Math.pow(0.25, dt); // floor friction
      }
      // roll as it moves
      b.angle += ((b.vx * dt) / (Math.min(b.w, b.h) / 2)) * (180 / Math.PI);
    }

    // simple circle collisions between loose icons
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i];
        const c = list[j];
        const ra = Math.min(a.w, a.h) / 2;
        const rc = Math.min(c.w, c.h) / 2;
        const dx = c.x + c.w / 2 - (a.x + a.w / 2);
        const dy = c.y + c.h / 2 - (a.y + a.h / 2);
        const dist = Math.hypot(dx, dy) || 0.001;
        const overlap = ra + rc - dist;
        if (overlap <= 0) continue;
        const nx = dx / dist;
        const ny = dy / dist;
        const aFixed = a.dragging;
        const cFixed = c.dragging;
        if (aFixed && cFixed) continue;

        // push apart
        const aShare = aFixed ? 0 : cFixed ? 1 : 0.5;
        const cShare = cFixed ? 0 : aFixed ? 1 : 0.5;
        a.x -= nx * overlap * aShare;
        a.y -= ny * overlap * aShare;
        c.x += nx * overlap * cShare;
        c.y += ny * overlap * cShare;

        // exchange momentum along the contact normal
        const rv = (c.vx - a.vx) * nx + (c.vy - a.vy) * ny;
        if (rv < 0) {
          const impulse = -(1 + BOUNCE) * rv;
          if (!aFixed && !cFixed) {
            a.vx -= (impulse / 2) * nx; a.vy -= (impulse / 2) * ny;
            c.vx += (impulse / 2) * nx; c.vy += (impulse / 2) * ny;
          } else if (aFixed) {
            c.vx += impulse * nx; c.vy += impulse * ny;
          } else {
            a.vx -= impulse * nx; a.vy -= impulse * ny;
          }
        }
      }
    }

    list.forEach(render);
    frame.current = requestAnimationFrame(tick);
  }, []);

  // run the simulation only while something is loose
  useEffect(() => {
    if (loose.length === 0) return undefined;
    lastTime.current = null;
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [loose.length, tick]);

  const startDrag = (id, clientX, clientY) => {
    const b = bodies.current.get(id);
    if (!b) return;
    b.dragging = true;
    b.vx = 0;
    b.vy = 0;
    drag.current = {
      id,
      offX: clientX - b.x,
      offY: clientY - b.y,
      samples: [{ x: clientX, y: clientY, t: performance.now() }],
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = drag.current;
      if (!d) return;
      const b = bodies.current.get(d.id);
      if (!b) return;
      const t = performance.now();
      d.samples.push({ x: e.clientX, y: e.clientY, t });
      while (d.samples.length > 2 && t - d.samples[0].t > SAMPLE_WINDOW) d.samples.shift();
      const first = d.samples[0];
      const span = Math.max((t - first.t) / 1000, 0.001);
      b.vx = (e.clientX - first.x) / span;
      b.vy = (e.clientY - first.y) / span;
      b.x = e.clientX - d.offX;
      b.y = e.clientY - d.offY;
      render(b);
    };
    const onUp = () => {
      const d = drag.current;
      if (!d) return;
      const b = bodies.current.get(d.id);
      if (b) {
        const speed = Math.hypot(b.vx, b.vy);
        if (speed > MAX_SPEED) {
          b.vx = (b.vx / speed) * MAX_SPEED;
          b.vy = (b.vy / speed) * MAX_SPEED;
        }
        b.dragging = false;
        if (b.el) b.el.style.cursor = "grab";
      }
      drag.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  // pull an icon out of its slot and start dragging it
  const grab = (id, icon, e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    bodies.current.set(id, {
      x: rect.left,
      y: rect.top,
      w: rect.width,
      h: rect.height,
      vx: 0,
      vy: 0,
      angle: 0,
      snow: 0,
      dragging: false,
      el: null,
    });
    setLoose((prev) => [...prev, { id, src: icon.image, alt: icon.name, w: rect.width, h: rect.height }]);
    startDrag(id, e.clientX, e.clientY);
  };

  const regrab = (id, e) => {
    e.preventDefault();
    e.currentTarget.style.cursor = "grabbing";
    startDrag(id, e.clientX, e.clientY);
  };

  const attach = (id, el) => {
    const b = bodies.current.get(id);
    if (b && el) {
      b.el = el;
      b.img = el.firstChild;
      b.cap = el.lastChild;
      render(b);
    }
  };

  const reset = () => {
    bodies.current.clear();
    drag.current = null;
    setLoose([]);
  };

  const looseIds = new Set(loose.map((l) => l.id));

  return (
    <PlaygroundContext.Provider value={{ grab, looseIds }}>
      {children}
      {loose.map((l) => (
        <div
          key={l.id}
          ref={(el) => attach(l.id, el)}
          onPointerDown={(e) => regrab(l.id, e)}
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            width: l.w,
            height: l.h,
            zIndex: 50,
            cursor: "grabbing",
            touchAction: "none",
            userSelect: "none",
            willChange: "transform",
          }}
        >
          <img
            src={l.src}
            alt={l.alt}
            title={l.alt}
            draggable={false}
            style={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "8%",
              right: "8%",
              bottom: "calc(100% - 4px)",
              height: 0,
              opacity: 0,
              background: "#ffffff",
              borderRadius: "50% 50% 40% 40% / 75% 75% 25% 25%",
              boxShadow: "0 1px 2px rgba(60, 80, 110, 0.35)",
              transition: "opacity 0.3s",
              pointerEvents: "none",
            }}
          />
        </div>
      ))}
      {loose.length > 0 && (
        <button
          type="button"
          onClick={reset}
          className="fixed bottom-5 right-5 z-[60] rounded-full bg-[#eb4d5c] px-4 py-2 text-base font-medium text-white shadow-lg hover:bg-[#d63c4b] dark:bg-[#1d3072] dark:hover:bg-[#2a3f8f]"
        >
          Put the icons back
        </button>
      )}
    </PlaygroundContext.Provider>
  );
}

// An icon that can be grabbed and thrown when inside an IconPlayground.
export function ThrowableIcon({ id, icon }) {
  const ctx = useContext(PlaygroundContext);
  if (!ctx) {
    return <img src={icon.image} alt={icon.name} title={icon.name} />;
  }
  const isLoose = ctx.looseIds.has(id);
  return (
    <img
      src={icon.image}
      alt={icon.name}
      title={icon.name}
      draggable={false}
      onPointerDown={(e) => !isLoose && ctx.grab(id, icon, e)}
      style={{
        visibility: isLoose ? "hidden" : "visible",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
      }}
    />
  );
}
